import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Stack,
  Text,
  DetailsList,
  DetailsListLayoutMode,
  SelectionMode,
  MessageBar,
  MessageBarType,
  Spinner,
  DefaultButton,
  Dialog,
  TextField,
  PrimaryButton,
  FontIcon,
} from "@fluentui/react";
import { styles } from "./ProposalDashboard";

/**
 * ProjectsManager (Compass Projects)
 * - Lists projects with search/filter/sort/pagination
 * - Create / Update in a right-side Panel
 * - Delete with confirmation dialog
 * - Uses /api/compass_projects and /api/clients
 */
export default function VectorStores(props) {
  // table state
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [newStoreDialogOpen, setNewStoreDialogOpen] = useState(false);
  const [newStoreState, setNewStoreState] = useState({ name: "", description: "" });



  // delete dialog
  // const [deleteTarget, setDeleteTarget] = useState(null); // {id, title}

  // toast
  const [toast, setToast] = useState(null); // {type, text}

  // columns
  const columns = useMemo(() => {
    return [
      { 
        key: "name", 
        name: "Name", 
        fieldName: "name", 
        maxWidth: 200,
        minWidth: 200,
        width: 200,
        isResizable: false,
        onRender: (item) => (<a href="#" onClick={(e) => {
          e.preventDefault();
          props.history.push(`/ai/rag-assistant/${item.id}`);
         }}>{item.name}</a>) 
      },
      { 
        key: "description", 
        name: "Description",
        fieldName: "description", 
        minWidth: 200,
        isResizable: true
      },
      {
        key: 'status',
        name: 'Status',
        fieldName: 'status',
      },
      { 
        key: "fileCount", 
        name: "File Count",
        fieldName: "completed",
        onRender: (item) => item.file_counts.completed || 0,
        maxWidth: 100,
        minWidth: 100,
      }
    ];
  }, []);

  // fetch projects list
  const getVectors = useCallback(async () => {

    setLoading(true);
    setError("");

    try {
      const res = await axios.get("/api/vector-store");
      setItems(res.data.vectorStores || []);
    } catch (e) {
      setError(e?.response?.data?.error || e.message || "Failed to load projects");
    } finally {
      setLoading(false);
    }
  }, []);

  const createVectorStore = async () => {
    try {
      const res = await axios.post("/api/vector-store/create", {
        name: newStoreState.name,
        description: newStoreState.description
      });

      setNewStoreDialogOpen(false);
      setNewStoreState({ name: "", description: "" });
      getVectors();
    } catch (e) {
      setError(e?.response?.data?.error || e.message || "Failed to create vector store");
    }
  };

  useEffect(() => { getVectors(); }, []);


  return (
    <Stack tokens={{ childrenGap: 24 }} className={styles.root}>
      <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
        <Stack tokens={{ childrenGap: 6 }}>
          <Text variant="xxLarge">
            <FontIcon iconName='SearchData' style={{marginRight: 8}} />
            Vector Stores
          </Text>
          <Text variant="small">
            Vector stores are places where you can upload files, and use RAG to query them.
          </Text>
        </Stack>
        <DefaultButton iconProps={{ iconName: "Add" }} text="New Store" onClick={() => setNewStoreDialogOpen(true)} />
      </Stack>

      {toast && (
        <MessageBar messageBarType={toast.type} isMultiline={false} onDismiss={() => setToast(null)}>
          {toast.text}
        </MessageBar>
      )}

      {error && (
        <MessageBar messageBarType={MessageBarType.error} isMultiline={false} onDismiss={() => setError("")}>{error}</MessageBar>
      )}

      <div style={styles.sectionCard}>
        {loading ? (
          <Stack horizontal horizontalAlign="center" styles={{ root: { padding: 40 } }}>
            <Spinner label="Loading vectors" />
          </Stack>
        ) : (
          <DetailsList
            items={items}
            columns={columns}
            setKey="set"
            layoutMode={DetailsListLayoutMode.justified}
            selectionMode={SelectionMode.none}
            compact
          />
        )}
      </div>

      {newStoreDialogOpen && (
        <Dialog hidden={!newStoreDialogOpen} onDismiss={() => setNewStoreDialogOpen(false)} dialogContentProps={{title: "Create New Vector Store"}}>
          <Stack tokens={{ childrenGap: 12 }}>
            <TextField value={newStoreState.name} label="Name" onChange={(e, val) => setNewStoreState({...newStoreState, name: val})} placeholder="Name" />
            <TextField value={newStoreState.description} label="Description" onChange={(e, val) => setNewStoreState({...newStoreState, description: val})} placeholder="Description" multiline rows={4} />
            <PrimaryButton text="Create" onClick={() => createVectorStore()} />
          </Stack>
        </Dialog>
      )}

    </Stack>
  );
}

